/** Create / edit dialog for a language policy (react-hook-form + zod for the
 *  scalar fields, plus a local-state ordered-rules editor).
 *
 *  A language policy is an ordered list of rules; each rule yields a language
 *  code (or null) and the first non-null result wins. A rule's `type` selects a
 *  type-aware definition input (JSONPath / dotty / template / function /
 *  constant language / notification block / nested policy). Rows reorder with
 *  the up/down buttons and the array order is submitted as each rule's `order`.
 *  `rules` is reconciled wholesale on write. `tenant` is clearable: cleared = a
 *  system-wide policy (`tour_operator: null`); the scope is also passed to the
 *  rule pickers so they search the right operator's catalog. */
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import {
  create as createPolicy,
  update as updatePolicy,
  listLanguagePolicyFunctions,
  listLanguages,
  RULE_TYPES,
  TEMPLATE_LANGUAGES,
  type LanguageOption,
  type PolicyDetail,
  type PolicyRef,
  type PolicyUnderTest,
  type PolicyWrite,
  type RuleType,
  type RuleWrite,
  type TemplateLanguage,
  type BlockRef,
} from '@/api/languagePolicies';
import * as policiesApi from '@/api/languagePolicies';
import * as blocksApi from '@/api/notificationBlocks';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';
import { BLOCK_HELP, POLICY_HELP } from './fieldHelp';
import { LanguagePolicyTestPanel } from './LanguagePolicyTestPanel';

/** One editor row (local state). The meaningful field depends on `type`:
 *  `definition` (jsonpath / dotty / template / function / constant code),
 *  `block` (block), `nestedPolicy` (policy). `key` is a stable React key
 *  for reorder/remove. */
interface EditorRow {
  key: string;
  type: RuleType;
  definition: string;
  template_language: TemplateLanguage;
  block: BlockRef | null;
  nestedPolicy: PolicyRef | null;
}

let keySeq = 0;
const nextKey = () => `rule-${keySeq++}`;

function toRows(detail: PolicyDetail | null): EditorRow[] {
  if (!detail) return [];
  return detail.rules.map((r) => ({
    key: nextKey(),
    type: r.type,
    definition: r.definition ?? '',
    template_language: r.template_language ?? 'django',
    block: r.block,
    nestedPolicy: r.nested_policy,
  }));
}

/** Editor rows → ordered RuleWrite[], dropping rows whose meaningful field is
 *  unfilled. Shared by the submit payload and the test panel so the tested
 *  policy is exactly what would be saved. */
function buildRules(rows: EditorRow[]): RuleWrite[] {
  const rules: RuleWrite[] = [];
  rows.forEach((r, i) => {
    const base = { type: r.type, order: i } as const;
    if (r.type === 'block') {
      if (r.block) rules.push({ ...base, block_id: r.block.id });
    } else if (r.type === 'policy') {
      if (r.nestedPolicy) rules.push({ ...base, nested_policy_id: r.nestedPolicy.id });
    } else if (r.type === 'template') {
      if (r.definition.trim())
        rules.push({ ...base, definition: r.definition, template_language: r.template_language });
    } else if (r.definition.trim()) {
      rules.push({ ...base, definition: r.definition.trim() });
    }
  });
  return rules;
}

const FormSchema = z.object({
  codename: z
    .string()
    .trim()
    .min(1, 'Codename is required')
    .max(128)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      'Letters, digits and underscore only; cannot start with a digit.',
    ),
  name: z.string().max(256),
  description: z.string(),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { codename: '', name: '', description: '', enabled: true };

function toDefaults(policy: PolicyDetail | null): FormValues {
  if (!policy) return EMPTY;
  return {
    codename: policy.codename,
    name: policy.name ?? '',
    description: policy.description ?? '',
    enabled: policy.enabled,
  };
}

const optionLabel = (o: { codename: string; name?: string }) =>
  o.name ? `${o.codename} — ${o.name}` : o.codename;

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  policy: PolicyDetail | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function LanguagePolicyFormDialog({ open, policy, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = policy !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.language_policy',
    objectPk: policy?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['language_policies'] }),
  });

  // Tenant + ordered rules live outside RHF (self-contained controlled inputs).
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [rows, setRows] = useState<EditorRow[]>([]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(policy),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(policy));
    setRows(toRows(policy));
    if (policy) {
      setTenant(
        policy.tenant
          ? { id: policy.tenant.id, name: policy.tenant.name, code: policy.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id]);

  const tenantId = tenant?.id ?? null;
  const editingId = policy?.id ?? null;

  // Registered predefined functions — gate the "Function" rule type + populate
  // its picker (empty → the type is disabled).
  const { data: functionNames } = useQuery({
    queryKey: ['language-policy-functions'],
    queryFn: listLanguagePolicyFunctions,
    staleTime: 60_000,
  });
  const functions = functionNames ?? [];
  const functionsEmpty = functionNames !== undefined && functions.length === 0;

  // Selectable languages for constant rules — tenant's enabled languages first.
  const { data: languagesData } = useQuery({
    queryKey: ['languages', tenantId],
    queryFn: () => listLanguages(tenantId),
    staleTime: 60_000,
  });
  const languages = languagesData ?? [];

  const patchRow = (key: string, patch: Partial<EditorRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const setType = (key: string, type: RuleType) =>
    patchRow(key, {
      type,
      definition: '',
      template_language: 'django',
      block: null,
      nestedPolicy: null,
    });
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      {
        key: nextKey(),
        type: 'jsonpath',
        definition: '',
        template_language: 'django',
        block: null,
        nestedPolicy: null,
      },
    ]);
  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));
  const moveRow = (index: number, dir: -1 | 1) =>
    setRows((rs) => {
      const target = index + dir;
      if (target < 0 || target >= rs.length) return rs;
      const next = [...rs];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });

  const mutation = useMutation({
    mutationFn: (body: PolicyWrite) =>
      isEdit ? updatePolicy(policy.id, body) : createPolicy(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Language policy updated.' : 'Language policy created.', 'success');
      qc.invalidateQueries({ queryKey: ['language_policies'] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    mutation.mutate({
      tour_operator: tenantId,
      codename: v.codename.trim(),
      name: v.name.trim(),
      description: v.description.trim(),
      enabled: v.enabled,
      rules: buildRules(rows),
    });
  });

  // Snapshot of the current editor state as an (unsaved) policy payload — used by
  // the test panel to evaluate exactly what the user is editing.
  const buildPolicyPayload = (): PolicyUnderTest => ({
    tour_operator: tenantId,
    rules: buildRules(rows),
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        <>
          {isEdit ? `Edit language policy · ${policy.codename}` : 'New language policy'}{' '}
          <HelpLink story="language-policies" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create policy'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            {...register('codename')}
            label="Codename"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.codename)}
            helperText={errors.codename?.message ?? 'Identifier used to reference this policy.'}
            InputProps={{ endAdornment: <FieldHelp help={POLICY_HELP.codename} /> }}
          />
          <TextField
            {...register('name')}
            label="Name"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.name)}
            helperText={errors.name?.message ?? 'Optional human-readable label.'}
          />
        </Box>

        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message ?? 'Optional.'}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <TenantPicker
              value={tenant}
              featured={isEdit ? undefined : pageTenants}
              onChange={setTenant}
              label="Operator"
              placeholder="System / global (no operator)"
            />
          </Box>
          <FieldHelp help={POLICY_HELP.operator} />
        </Box>

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={labelWithHelp('Enabled', POLICY_HELP.enabled)}
            />
          )}
        />

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Rules <FieldHelp help={POLICY_HELP.rules} />
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Evaluated top to bottom — each rule yields a language code (or nothing) and the first
            non-null result wins. Order is preserved on save.
          </Typography>

          <Stack spacing={1.5}>
            {rows.map((row, i) => (
              <RuleRow
                key={row.key}
                row={row}
                index={i}
                total={rows.length}
                tenantId={tenantId}
                editingId={editingId}
                functions={functions}
                functionsEmpty={functionsEmpty}
                languages={languages}
                onPatch={(patch) => patchRow(row.key, patch)}
                onType={(type) => setType(row.key, type)}
                onMove={(dir) => moveRow(i, dir)}
                onRemove={() => removeRow(row.key)}
              />
            ))}
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No rules yet.
              </Typography>
            )}
          </Stack>

          <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1.5 }} onClick={addRow}>
            Add rule
          </Button>
        </Box>

        <Divider />

        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant="subtitle2">Test this policy</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <LanguagePolicyTestPanel buildPolicy={buildPolicyPayload} />
          </AccordionDetails>
        </Accordion>
      </Box>
      )}
    </AppDialog>
  );
}

interface RuleRowProps {
  row: EditorRow;
  index: number;
  total: number;
  tenantId: number | null;
  editingId: number | null;
  functions: string[];
  functionsEmpty: boolean;
  languages: LanguageOption[];
  onPatch: (patch: Partial<EditorRow>) => void;
  onType: (type: RuleType) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

function RuleRow({
  row,
  index,
  total,
  tenantId,
  editingId,
  functions,
  functionsEmpty,
  languages,
  onPatch,
  onType,
  onMove,
  onRemove,
}: RuleRowProps) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <TextField
          select
          label="Type"
          size="small"
          sx={{ width: 200 }}
          value={row.type}
          onChange={(e) => onType(e.target.value as RuleType)}
        >
          {RULE_TYPES.map((t) => {
            const disabled = t.value === 'function' && functionsEmpty;
            return (
              <MenuItem key={t.value} value={t.value} disabled={disabled}>
                {t.label}
                {disabled ? ' (none registered)' : ''}
              </MenuItem>
            );
          })}
        </TextField>
        <FieldHelp help={POLICY_HELP.ruleType} />

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          #{index + 1}
        </Typography>
        <IconButton
          aria-label="Move up"
          size="small"
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Move down"
          size="small"
          disabled={index === total - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label="Remove rule" size="small" onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <RuleDefinitionInput
        row={row}
        tenantId={tenantId}
        editingId={editingId}
        functions={functions}
        functionsEmpty={functionsEmpty}
        languages={languages}
        onPatch={onPatch}
      />
    </Box>
  );
}

interface RuleDefinitionInputProps {
  row: EditorRow;
  tenantId: number | null;
  editingId: number | null;
  functions: string[];
  functionsEmpty: boolean;
  languages: LanguageOption[];
  onPatch: (patch: Partial<EditorRow>) => void;
}

function RuleDefinitionInput({
  row,
  tenantId,
  editingId,
  functions,
  functionsEmpty,
  languages,
  onPatch,
}: RuleDefinitionInputProps) {
  if (row.type === 'constant') {
    // The languages list is the full ISO 639-1 set (tenant-enabled first); keep
    // the current value selectable even before the list resolves.
    const options =
      row.definition && !languages.some((l) => l.code === row.definition)
        ? [{ code: row.definition, name: row.definition, enabled: false }, ...languages]
        : languages;
    return (
      <TextField
        select
        label="Language"
        size="small"
        fullWidth
        value={row.definition}
        onChange={(e) => onPatch({ definition: e.target.value })}
        helperText="The language code this rule always yields."
      >
        {options.map((l) => (
          <MenuItem key={l.code} value={l.code}>
            {l.name} ({l.code})
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (row.type === 'function') {
    // Keep an out-of-registry current value selectable (mirrors the block editor).
    const options =
      row.definition && !functions.includes(row.definition)
        ? [row.definition, ...functions]
        : functions;
    return (
      <TextField
        select
        label="Function"
        size="small"
        fullWidth
        disabled={functionsEmpty}
        value={row.definition}
        onChange={(e) => onPatch({ definition: e.target.value })}
        helperText={
          functionsEmpty
            ? 'No predefined functions are registered.'
            : 'Name of a registered resolver function.'
        }
      >
        {options.map((fn) => (
          <MenuItem key={fn} value={fn}>
            {fn}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (row.type === 'block') {
    return (
      <RemoteAutocomplete<BlockRef>
        label="Block"
        size="small"
        fetchOnEmpty
        value={row.block}
        getOptionLabel={optionLabel}
        getOptionKey={(o) => o.id}
        queryFn={async (q) => {
          const r = await blocksApi.list({
            search: q,
            pageSize: 20,
            ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
          });
          return r.results.map((b) => ({
            id: b.id,
            codename: b.codename,
            name: b.name,
            type: b.type,
          }));
        }}
        onChange={(v) => onPatch({ block: v as BlockRef | null })}
      />
    );
  }

  if (row.type === 'policy') {
    return (
      <RemoteAutocomplete<PolicyRef>
        label="Nested policy"
        size="small"
        fetchOnEmpty
        value={row.nestedPolicy}
        getOptionLabel={optionLabel}
        getOptionKey={(o) => o.id}
        queryFn={async (q) => {
          const r = await policiesApi.list({
            search: q,
            pageSize: 20,
            ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
          });
          return (
            r.results
              // A policy can never nest itself (the backend rejects cycles).
              .filter((p) => p.id !== editingId)
              .map((p) => ({ id: p.id, codename: p.codename, name: p.name }))
          );
        }}
        onChange={(v) => onPatch({ nestedPolicy: v as PolicyRef | null })}
      />
    );
  }

  if (row.type === 'template') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label="Template source"
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={row.definition}
          onChange={(e) => onPatch({ definition: e.target.value })}
          helperText="Rendered per event; the output is treated as the language code."
          InputProps={{ endAdornment: <FieldHelp help={POLICY_HELP.ruleTemplate} /> }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            select
            label="Template language"
            size="small"
            sx={{ width: 200 }}
            value={row.template_language}
            onChange={(e) => onPatch({ template_language: e.target.value as TemplateLanguage })}
          >
            {TEMPLATE_LANGUAGES.map((l) => (
              <MenuItem key={l.value} value={l.value}>
                {l.label}
              </MenuItem>
            ))}
          </TextField>
          <FieldHelp help={BLOCK_HELP.templateLanguage} />
        </Box>
      </Box>
    );
  }

  // jsonpath / dotty — single-line path expression.
  return (
    <TextField
      label={row.type === 'jsonpath' ? 'JSONPath expression' : 'Dotty path'}
      size="small"
      fullWidth
      value={row.definition}
      onChange={(e) => onPatch({ definition: e.target.value })}
      helperText={
        row.type === 'jsonpath'
          ? 'A JSONPath expression, e.g. $.booking.guest.language.'
          : 'Dot-separated lookup path, e.g. booking.guest.language.'
      }
      InputProps={{
        endAdornment: (
          <FieldHelp
            help={row.type === 'jsonpath' ? POLICY_HELP.ruleJsonpath : POLICY_HELP.ruleDotty}
          />
        ),
      }}
    />
  );
}
